import PropTypes from 'prop-types'
import { useSelect } from 'downshift'
import { forwardRef } from 'react'
import { hideVisually } from 'polished'

import Text from 'components/common/Text'
import { DropdownItem } from 'components/common/Dropdown'
import { sortBy } from 'utils/list'
import theme from 'theme'
import Icon from 'components/Icon'

const Menu = forwardRef(({ isOpen, ...rest }, ref) => (
  <ul
    ref={ref}
    css={[
      !isOpen && hideVisually(),

      {
        position: 'absolute',
        right: 0,
        top: 'calc(100% + 4px)',
        minWidth: theme.space[8],
        border: theme.border.base,
        paddingTop: theme.space[2],
        paddingBottom: theme.space[2],
        borderRadius: theme.borderRadius[0],
        backgroundColor: 'white',
        zIndex: theme.zIndices[1],
        maxHeight: 300,
        overflowY: 'auto',
        textAlign: 'left',
        listStyle: 'none',
        boxShadow: theme.boxShadows.small
      },

      isOpen && {
        borderColor: theme.colors.gray[1]
      }
    ]}
    {...rest}
  />
))

Menu.propTypes = {
  isOpen: PropTypes.bool
}

export function FilterSelect({
  getFilterDisplayTitle = label => label,
  label = '',
  items,
  selectedValue,
  onSelected,
  hideLabel,
  textColor,
  ...rest
}) {
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useSelect({
    items,
    selectedItem: selectedValue,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelected(selectedItem.value)
    }
  })

  const valuesByLabel = Object.fromEntries(
    (items || []).map(item => [item.value, item.label])
  )

  return (
    <div
      css={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr)',
        gap: theme.space[2]
      }}
      {...rest}
    >
      {!hideLabel && (
        <label {...getLabelProps()}>
          <Text as="small">{getFilterDisplayTitle(label)}:</Text>
        </label>
      )}
      <div
        data-test-filter-select-trigger
        aria-roledescription="button"
        css={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumn: 'auto',
          justifyContent: 'start',
          alignItems: 'center',
          gap: theme.space[2],
          cursor: 'pointer'
        }}
        {...getToggleButtonProps()}
      >
        <Text
          overflowEllipsis
          as="p"
          color="medium"
          css={{
            color: textColor,
            fontWeight: theme.fontWeights[1],
            textTransform: 'uppercase',
            fontSize: theme.fontSizes[0]
          }}
        >
          {valuesByLabel[selectedValue] || 'All'}
        </Text>
        <Icon
          css={{
            fontSize: theme.fontSizes[0],
            color: textColor || theme.colors.steel[1]
          }}
          icon="caretDown"
        />
      </div>
      <Menu {...getMenuProps()} isOpen={isOpen}>
        {isOpen &&
          items.map((item, index) => (
            <DropdownItem
              key={item.value}
              getItemProps={getItemProps}
              highlightedIndex={highlightedIndex}
              index={index}
              item={item}
              selectedValue={selectedValue || 'All'}
            />
          ))}
      </Menu>
    </div>
  )
}

FilterSelect.propTypes = {
  label: PropTypes.string,
  items: PropTypes.array,
  selectedValue: PropTypes.any,
  onSelected: PropTypes.func,
  getFilterDisplayTitle: PropTypes.func,
  hideLabel: PropTypes.bool,
  textColor: PropTypes.string
}

export default function FilterOptions({
  filterOptions = {},
  getFilterDisplayTitle,
  getItemValue = x => x,
  getItemLabel = x => x,
  values,
  onSelection
}) {
  const optionEntries = Object.entries(filterOptions)

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: `repeat(${optionEntries.length}, minmax(64px, 128px))`,
        gap: theme.space[3],
        justifyItems: 'stretch',
        alignItems: 'center'
      }}
    >
      {optionEntries.map(([key, { options, sortOrder = 'asc' }]) => {
        return (
          <FilterSelect
            key={key}
            getFilterDisplayTitle={getFilterDisplayTitle}
            items={[{ label: 'All', value: 'All' }].concat(
              sortBy(
                Array.from(options).map(item => ({
                  label: getItemLabel(item),
                  value: getItemValue(item)
                })),
                ({ label }) => label,
                sortOrder
              )
            )}
            label={key}
            selectedValue={values[key]}
            onSelected={value => {
              if (value === 'All') {
                const copy = Object.assign({}, values)
                delete copy[key]
                onSelection(copy)
              } else {
                onSelection({ ...values, [key]: value })
              }
            }}
          />
        )
      })}
    </div>
  )
}

FilterOptions.propTypes = {
  filterOptions: PropTypes.object,
  values: PropTypes.object,
  onSelection: PropTypes.func,
  getFilterDisplayTitle: PropTypes.func,
  getItemValue: PropTypes.func,
  getItemLabel: PropTypes.func
}
